import React, { useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import * as ROUTES from "../../Constants/Routes";
import { BiX } from "react-icons/bi";
import { AiOutlineMeh } from "react-icons/ai";
import { useTranslation, Trans } from "react-i18next";

interface ErrorProps {
  floating?: boolean;
  children?: ReactNode;
  redirect?: boolean;
  redirectInSeconds?: number;
  errorHeadline?: string;
  height?: string | number;
}

interface DefaultContentProps {
  errorHeadline: string;
  redirect?: boolean;
  redirectInSeconds: number;
}

const Error: React.FC<ErrorProps> = ({
  floating = false,
  children,
  redirect = false,
  redirectInSeconds = 5,
  errorHeadline = "error_oops",
  height,
}) => {
  const { t } = useTranslation();

  const [errorClass, setErrorClass] = useState<string>(floating ? "floating" : "");

  useEffect(() => {
    if (floating) {
      setErrorClass((prev) => `${prev} slide-in`);
    }
  }, [floating]);

  const handleClose = useCallback(() => {
    setErrorClass((prev) => `${prev} slide-out`);
  }, []);

  const containerStyle = useMemo(
    () => ({ height: height ?? undefined }),
    [height]
  );

  return (
    <div className={`error ${errorClass}`} style={containerStyle}>
      {!floating && (
        <div className="error__icon">
          <AiOutlineMeh className="icon" />
        </div>
      )}

      <div className="error__content">
        {children ?? (
          <DefaultContent
            redirect={redirect}
            redirectInSeconds={redirectInSeconds}
            errorHeadline={t(errorHeadline)}
          />
        )}

        {floating && (
          <button type="button" className="error__close" onClick={handleClose}>
            <BiX className="icon" />
          </button>
        )}
      </div>
    </div>
  );
};

const DefaultContent: React.FC<DefaultContentProps> = ({
  errorHeadline,
  redirect = false,
  redirectInSeconds,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [timeLeft, setTimeLeft] = useState<number>(redirectInSeconds);

  // Redirect timer
  useEffect(() => {
    if (!redirect) return;

    const timeoutId = setTimeout(() => {
      history.push(ROUTES.HOME);
    }, redirectInSeconds * 1000);

    return () => clearTimeout(timeoutId);
  }, [redirect, redirectInSeconds, history]);

  // Countdown timer
  useEffect(() => {
    if (!redirect) return;

    const intervalId = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [redirect]);

  const titleStyle = useMemo(
    () => ({ marginBottom: redirect ? "15px" : undefined }),
    [redirect]
  );

  return (
    <>
      <h5 className="error__title" style={titleStyle}>
        {errorHeadline}
      </h5>

      <p className="error__message">{t("error_unknown")}</p>

      {redirect && (
        <p className="error__redirect-message">
          <Trans
            i18nKey="redirection_notice_homepage"
            values={{ timeLeft }}
          >
            You will be redirected to <a href={ROUTES.HOME}>homepage</a> in
            [timeLeft] seconds.
          </Trans>
        </p>
      )}
    </>
  );
};

export default Error;
